import { isNumber, isString } from 'shared/utils/type-guards'
import { toCurrencyUnicodeMap } from './currencyCodesMap'
import { CurrentUser } from '~/views/contexts/CurrentUserContext'
import {
  CountryCodeEnumType,
  CurrencyCodeEnumType,
} from '~/common/types/graphql'

export function getCodesFromCurrentUser(
  currentUser: CurrentUser | null | undefined,
) {
  const currencyCode = currentUser?.organization?.currencyCode

  const countryCode = currentUser?.organization?.countryCode

  return { currencyCode, countryCode }
}

interface ToLocalizedCurrencyNumberProps {
  number: number | string
  countryCode?: CountryCodeEnumType
  currencyCode?: CurrencyCodeEnumType
  displayFractionDigits?: boolean
}

export function toLocalizedCurrencyNumber({
  number,
  countryCode = CountryCodeEnumType.En,
  currencyCode = CurrencyCodeEnumType.Usd,
  displayFractionDigits = false,
}: ToLocalizedCurrencyNumberProps) {
  if (
    (number !== 0 && !isNumber(number) && !isString(number)) ||
    isNaN(Number(number))
  ) {
    return ''
  }
  return (
    Number(number).toLocaleString(countryCode, {
      style: 'currency',
      currency: currencyCode,
      maximumFractionDigits: displayFractionDigits ? undefined : 0, // <-- undefined; browser spec figures out what to put here; let it
      minimumFractionDigits: displayFractionDigits ? undefined : 0, // <-- if `maximumFractionDigits` is set to zero,  must set `minimumFractionDigits` to zero,  to avoid a range error: https://stackoverflow.com/questions/41045270/range-error-with-tolocalestring-with-maximumnumber-of-digits-0
    }) || number
  )
}

export function getCurrencySymbol(
  currencyCode: CurrencyCodeEnumType = CurrencyCodeEnumType.Usd,
) {
  if (
    !currencyCode ||
    !isString(currencyCode) ||
    !toCurrencyUnicodeMap[currencyCode]
  ) {
    return ''
  }
  return toCurrencyUnicodeMap[currencyCode]
}

function decimalPointToFloat(value: string | number): number {
  return Number(
    value
      .toString()
      .trim()
      .replace(/[^\d,.]/g, '')
      .replaceAll(',', ''),
  )
}

function decimalCommaToFloat(value: string | number) {
  return Number(
    value
      .toString()
      .trim()
      .replace(/[^\d,.]/g, '')
      .replaceAll('.', '')
      .replaceAll(',', '.'),
  )
}

export type toFloatParams = {
  value: string | number | undefined
  fromCurrency?: CurrencyCodeEnumType
}

export const toFloat = ({
  value,
  fromCurrency = CurrencyCodeEnumType.Usd,
}: toFloatParams): number => {
  if (!value) return 0
  let floatValue = 0
  switch (fromCurrency) {
    case 'EUR':
    case 'CHF':
    case 'BRL':
      floatValue = decimalCommaToFloat(value)
      break
    default:
      floatValue = decimalPointToFloat(value)
      break
  }

  return floatValue
}
